import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentPlusIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { filingDebtors, sortDebtorsPrimaryFirst } from '../../utils/debtors';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const inputClass =
  'mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500';

function requestHeaders(json = false) {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function responseData(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = typeof data?.detail === 'string'
      ? data.detail
      : data?.detail
        ? JSON.stringify(data.detail)
        : `Request failed (${response.status})`;
    throw new Error(detail);
  }
  return data;
}

function fullName(debtor) {
  return [debtor?.first_name, debtor?.second_name, debtor?.last_name, debtor?.suffix]
    .filter(Boolean)
    .join(' ')
    .trim();
}

function debtorNumber(debtor) {
  return debtor?.is_primary ? 1 : 2;
}

function hasPositiveAmount(value) {
  return Number(value || 0) > 0;
}

function normalizedTemplateText(template) {
  return [
    template?.name,
    template?.description,
    template?.default_checklist_name,
    template?.default_checklist_description,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function findDsoTemplate(templates, chapter, number) {
  const activeTemplates = (templates || []).filter((template) => template?.is_active !== false);
  const chapterPattern = new RegExp(`\\b(?:ch|chapter)\\s*${chapter}\\b`, 'i');
  const debtorPattern = new RegExp(`\\bdebtor\\s*${number}\\b`, 'i');
  const chapterAndDebtor = activeTemplates.filter((template) => {
    const text = normalizedTemplateText(template);
    return chapterPattern.test(text) && debtorPattern.test(text);
  });

  return chapterAndDebtor.find((template) => (
    /\bdso\b|domestic support obligation/i.test(normalizedTemplateText(template))
  )) || chapterAndDebtor[0] || null;
}

function payrollDsoForDebtor(debtor, intakeData) {
  const hasSavedDeduction = (debtor?.employments || []).some((employment) => (
    (employment?.deductions || []).some((deduction) => (
      deduction?.deduction_type === 'dso' && hasPositiveAmount(deduction?.amount)
    ))
  ));
  const intakeField = debtor?.is_primary
    ? intakeData?.childSupportDeducted
    : intakeData?.spouseChildSupportDeducted;
  return hasSavedDeduction || String(intakeField || '').toLowerCase() === 'yes';
}

export function dsoTargetContext(caseData, expenses) {
  const debtors = sortDebtorsPrimaryFirst(caseData?.debtors);
  const intakeData = caseData?.intake_data || {};
  const filingDebtorOptions = filingDebtors(debtors);
  const payrollDebtors = filingDebtorOptions.filter(
    (debtor) => payrollDsoForDebtor(debtor, intakeData),
  );
  const hasScheduleJExpense = (expenses || []).some((expense) => (
    expense?.category === 'alimony_child_support' && hasPositiveAmount(expense?.amount)
  )) || [intakeData.childSupportAmount, intakeData.spouseChildSupportAmount]
    .some(hasPositiveAmount);
  const selectableDebtors = filingDebtorOptions;

  return {
    debtors,
    payrollDebtors,
    hasScheduleJExpense,
    source: payrollDebtors.length ? 'payroll' : 'schedule_j',
    targetDebtors: payrollDebtors.length ? payrollDebtors : selectableDebtors,
  };
}

function normalizeSchema(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((field) => field && typeof field === 'object')
    .map((field) => ({
      key: String(field.key || '').trim(),
      label: String(field.label || field.key || '').trim(),
      type: String(field.type || 'text').toLowerCase(),
      required: Boolean(field.required),
      help: String(field.help || '').trim(),
      options: Array.isArray(field.options) ? field.options : [],
    }))
    .filter((field) => field.key && field.label);
}

function dsoPacketEntries(checklist) {
  return (checklist || [])
    .flatMap((item) => (item?.trustee_docs || []).map((packet) => ({ item, packet })))
    .filter(({ item, packet }) => (
      /\bdso\b|domestic support obligation/i.test(
        `${item?.name || ''} ${packet?.template?.name || ''} ${packet?.template?.description || ''}`,
      )
    ))
    .sort((left, right) => (
      new Date(right.packet.created_at || 0) - new Date(left.packet.created_at || 0)
    ));
}

function packetDebtorNumber(entry) {
  const payloadNumber = Number(entry?.packet?.payload?.DSO_DEBTOR_NUMBER);
  if ([1, 2].includes(payloadNumber)) return payloadNumber;
  const text = `${entry?.item?.name || ''} ${entry?.packet?.template?.name || ''}`;
  const match = text.match(/debtor\s*([12])/i);
  return match ? Number(match[1]) : null;
}

function packetForDebtor(entries, debtor, targetCount) {
  const debtorId = String(debtor?.id || '');
  const number = debtorNumber(debtor);
  const exact = entries.find((entry) => (
    debtorId && String(entry?.packet?.payload?.DSO_DEBTOR_ID || '') === debtorId
  ));
  if (exact) return exact;

  const numbered = entries.find((entry) => packetDebtorNumber(entry) === number);
  if (numbered) return numbered;

  const unassigned = entries.filter((entry) => !packetDebtorNumber(entry));
  return targetCount === 1 ? unassigned[0] || null : null;
}

function automaticPayload(debtor) {
  const name = fullName(debtor);
  return {
    DSO_DEBTOR_ID: String(debtor?.id || ''),
    DSO_DEBTOR_NUMBER: String(debtorNumber(debtor)),
    DSO_DEBTOR_FULL_NAME: name,
    DSO_DEBTOR_FIRST_NAME: debtor?.first_name || '',
    SELECTED_DEBTOR_FULL_NAME: name,
    SELECTED_DEBTOR_FIRST_NAME: debtor?.first_name || '',
    DEBTOR_FULL_NAME: name,
    DEBTOR_FIRST_NAME: debtor?.first_name || '',
  };
}

function initialPayload(template, debtor) {
  const payload = automaticPayload(debtor);
  normalizeSchema(template?.field_schema).forEach((field) => {
    if (!(field.key in payload)) payload[field.key] = field.type === 'checkbox' ? false : '';
  });
  return payload;
}

export default function DSOFormPanel({
  caseId,
  caseNumber = '',
  chapter,
  initialChecklist = null,
  initialCaseData = null,
  initialExpenses = null,
  todoId = null,
}) {
  const [checklist, setChecklist] = useState(
    Array.isArray(initialChecklist) ? initialChecklist : null,
  );
  const [caseData, setCaseData] = useState(initialCaseData);
  const [expenses, setExpenses] = useState(
    Array.isArray(initialExpenses) ? initialExpenses : null,
  );
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedDebtorId, setSelectedDebtorId] = useState('');
  const [payload, setPayload] = useState({});
  const [creating, setCreating] = useState(false);
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailComposer, setEmailComposer] = useState(null);
  const [loadingEmailContext, setLoadingEmailContext] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const context = useMemo(
    () => dsoTargetContext(caseData, expenses),
    [caseData, expenses],
  );
  const entries = useMemo(() => dsoPacketEntries(checklist), [checklist]);
  const selectedDebtor = context.targetDebtors.find(
    (debtor) => String(debtor.id) === String(selectedDebtorId),
  ) || null;
  const template = useMemo(() => (
    selectedDebtor
      ? findDsoTemplate(templates, String(chapter), debtorNumber(selectedDebtor))
      : null
  ), [templates, chapter, selectedDebtor]);
  const schema = useMemo(() => normalizeSchema(template?.field_schema), [template]);
  const currentEntry = selectedDebtor
    ? packetForDebtor(entries, selectedDebtor, context.targetDebtors.length)
    : null;

  const refreshChecklist = async () => {
    const response = await fetch(`${API_URL}/case/${caseId}/checklist/`, {
      headers: requestHeaders(),
    });
    const data = await responseData(response);
    const nextChecklist = Array.isArray(data) ? data : [];
    setChecklist(nextChecklist);
    return nextChecklist;
  };

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setLoadError('');
      const requests = [
        fetch(`${API_URL}/firm/trustee-doc-templates/`, { headers: requestHeaders() })
          .then(responseData),
        Array.isArray(initialChecklist)
          ? Promise.resolve(initialChecklist)
          : fetch(`${API_URL}/case/${caseId}/checklist/`, { headers: requestHeaders() })
            .then(responseData),
        initialCaseData
          ? Promise.resolve(initialCaseData)
          : fetch(`${API_URL}/case/${caseId}/overview/`, { headers: requestHeaders() })
            .then(responseData),
        Array.isArray(initialExpenses)
          ? Promise.resolve(initialExpenses)
          : fetch(`${API_URL}/cases/${caseId}/expenses/`, { headers: requestHeaders() })
            .then(responseData),
      ];

      const results = await Promise.allSettled(requests);
      if (!active) return;
      const errors = [];
      const [templateResult, checklistResult, caseResult, expenseResult] = results;
      if (templateResult.status === 'fulfilled') {
        const data = templateResult.value;
        setTemplates(Array.isArray(data) ? data : data?.results || []);
      } else errors.push(templateResult.reason?.message || 'Could not load DSO templates.');
      if (checklistResult.status === 'fulfilled') {
        setChecklist(Array.isArray(checklistResult.value) ? checklistResult.value : []);
      } else errors.push(checklistResult.reason?.message || 'Could not load case documents.');
      if (caseResult.status === 'fulfilled') setCaseData(caseResult.value);
      else errors.push(caseResult.reason?.message || 'Could not load debtor information.');
      if (expenseResult.status === 'fulfilled') {
        setExpenses(Array.isArray(expenseResult.value) ? expenseResult.value : []);
      } else errors.push(expenseResult.reason?.message || 'Could not load Schedule J expenses.');
      setLoadError(errors.join(' '));
      setLoading(false);
    }

    if (caseId) load();
    return () => { active = false; };
  }, [caseId, initialCaseData, initialChecklist, initialExpenses]);

  const targetKey = context.targetDebtors.map((debtor) => debtor.id).join(',');
  const packetKey = entries.map((entry) => entry.packet.id).join(',');
  useEffect(() => {
    setSelectedDebtorId((current) => {
      if (context.targetDebtors.some((debtor) => String(debtor.id) === String(current))) {
        return current;
      }
      if (context.source === 'schedule_j' && context.targetDebtors.length > 1) return '';
      const firstMissing = context.targetDebtors.find((debtor) => (
        !packetForDebtor(entries, debtor, context.targetDebtors.length)
      ));
      return String(firstMissing?.id || context.targetDebtors[0]?.id || '');
    });
  }, [targetKey, packetKey, context.source]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPayload(selectedDebtor && template ? initialPayload(template, selectedDebtor) : {});
  }, [selectedDebtor?.id, template?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const createForm = async (event) => {
    event.preventDefault();
    setActionError('');
    setSuccess('');
    if (!selectedDebtor) {
      setActionError('Choose the debtor who must complete the DSO Form.');
      return;
    }
    if (!template) {
      setActionError(`No active Chapter ${chapter} Debtor ${debtorNumber(selectedDebtor)} DSO Form template is available.`);
      return;
    }
    for (const field of schema) {
      const value = payload[field.key];
      if (field.required && (value === null || value === undefined || String(value).trim() === '')) {
        setActionError(`Please complete: ${field.label}`);
        return;
      }
    }

    setCreating(true);
    try {
      const number = debtorNumber(selectedDebtor);
      const name = fullName(selectedDebtor);
      const response = await fetch(
        `${API_URL}/firm/trustee-doc-templates/${template.id}/generate/`,
        {
          method: 'POST',
          headers: requestHeaders(true),
          body: JSON.stringify({
            case_id: caseId,
            payload: { ...automaticPayload(selectedDebtor), ...payload },
            checklist_name: `DSO Form - Debtor ${number}${name ? ` - ${name}` : ''}`,
            checklist_description: template.default_checklist_description
              || 'Please complete and sign the DSO Form, then upload the completed document here.',
            is_required: true,
          }),
        },
      );
      await responseData(response);
      const nextChecklist = await refreshChecklist();
      const nextEntries = dsoPacketEntries(nextChecklist);
      const nextMissing = context.targetDebtors.find((debtor) => (
        !packetForDebtor(nextEntries, debtor, context.targetDebtors.length)
      ));
      if (context.source === 'payroll' && nextMissing) {
        setSelectedDebtorId(String(nextMissing.id));
      }
      setSuccess(`DSO Form created for ${name || `Debtor ${number}`} and added to the document checklist.`);
    } catch (error) {
      setActionError(error.message || 'Could not create the DSO Form.');
    } finally {
      setCreating(false);
    }
  };

  const openGeneratedDocument = async (packet, kind) => {
    setActionError('');
    try {
      const response = await fetch(
        `${API_URL}/firm/trustee-doc-packets/${packet.id}/presign/?kind=${kind}`,
        { headers: requestHeaders() },
      );
      const data = await responseData(response);
      if (!data.url) throw new Error('No document link was returned.');
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      setActionError(error.message || 'Could not open the DSO Form.');
    }
  };

  const openEmailComposer = async (entry, debtor) => {
    setActionError('');
    setSuccess('');
    setLoadingEmailContext(true);
    try {
      const todoQuery = todoId ? `?todo_id=${encodeURIComponent(todoId)}` : '';
      const response = await fetch(
        `${API_URL}/case/${caseId}/trustee-doc-packets/${entry.packet.id}/email/${todoQuery}`,
        { headers: requestHeaders() },
      );
      const emailContext = await responseData(response);
      if (!emailContext.assigned_employee) {
        throw new Error('Assign this trustee-document todo to an employee before composing the email.');
      }
      const employeeFirstName = emailContext.assigned_employee.first_name;
      const debtorName = fullName(debtor) || `Debtor ${debtorNumber(debtor)}`;
      setEmailComposer({
        entry,
        debtor,
        assignedEmployee: emailContext.assigned_employee,
        to: (emailContext.debtor_emails || [debtor?.email]).filter(Boolean).join(', '),
        cc: (emailContext.attorney_cc || []).filter(Boolean).join(', '),
        subject: emailContext.subject || `DSO Form — Bankruptcy Case ${caseNumber || caseId}`,
        body: emailContext.body || `Hello ${debtor?.first_name || ''}!\n\nThis is ${employeeFirstName} at Cisowski Law.\n\nPlease complete and sign the attached DSO Form required for your Chapter ${chapter} bankruptcy case, then return the completed form to us.\n\nPlease let us know if you have any questions.\n\nSincerely,\n${employeeFirstName}\nCisowski Law`,
        debtorName,
      });
    } catch (error) {
      setActionError(error.message || 'Could not prepare the DSO Form email.');
    } finally {
      setLoadingEmailContext(false);
    }
  };

  const sendEmail = async (event) => {
    event.preventDefault();
    if (!emailComposer) return;
    setSendingEmail(true);
    setActionError('');
    try {
      const response = await fetch(
        `${API_URL}/case/${caseId}/trustee-doc-packets/${emailComposer.entry.packet.id}/email/`,
        {
          method: 'POST',
          headers: requestHeaders(true),
          body: JSON.stringify({
            to: emailComposer.to,
            cc: emailComposer.cc,
            subject: emailComposer.subject,
            body: emailComposer.body,
            todo_id: todoId,
          }),
        },
      );
      const data = await responseData(response);
      setEmailComposer(null);
      setSuccess(`DSO Form emailed to ${(data.to || []).join(', ')}.`);
      await refreshChecklist();
    } catch (error) {
      setActionError(error.message || 'Could not send the DSO Form email.');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading && (!caseData || checklist === null)) {
    return <div className="mt-4 text-sm text-slate-500">Preparing the DSO Form options…</div>;
  }

  return (
    <section className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/60 p-4 text-slate-700">
      <div>
        <h4 className="font-semibold text-slate-900">DSO Form</h4>
        <p className="mt-1 text-sm">
          {context.source === 'payroll'
            ? `A DSO payroll deduction identifies ${context.payrollDebtors.map(fullName).filter(Boolean).join(' and ')}.`
            : 'The DSO appears on Schedule J without identifying a debtor. Choose which debtor should receive the form.'}
        </p>
      </div>

      {loadError && <p className="mt-3 text-sm font-medium text-red-700">{loadError}</p>}
      {actionError && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <ExclamationTriangleIcon className="h-5 w-5 flex-none" /> {actionError}
        </div>
      )}
      {success && <p className="mt-3 text-sm font-medium text-green-700">{success}</p>}

      {context.targetDebtors.length > 1 ? (
        <label className="mt-4 block max-w-md text-sm font-medium text-slate-700">
          DSO Form for
          <select
            className={inputClass}
            value={selectedDebtorId}
            onChange={(event) => {
              setSelectedDebtorId(event.target.value);
              setActionError('');
              setSuccess('');
            }}
          >
            <option value="">— Choose debtor —</option>
            {context.targetDebtors.map((debtor) => (
              <option key={debtor.id} value={debtor.id}>
                Debtor {debtorNumber(debtor)} — {fullName(debtor) || 'Unnamed debtor'}
                {context.source === 'payroll' ? ' (payroll DSO)' : ''}
              </option>
            ))}
          </select>
        </label>
      ) : context.targetDebtors.length === 1 ? (
        <p className="mt-3 text-sm font-semibold text-slate-800">
          Debtor {debtorNumber(context.targetDebtors[0])} — {fullName(context.targetDebtors[0])}
        </p>
      ) : (
        <p className="mt-3 text-sm font-medium text-amber-800">No debtor is available for this DSO Form.</p>
      )}

      {context.targetDebtors.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {context.targetDebtors.map((debtor) => {
            const entry = packetForDebtor(entries, debtor, context.targetDebtors.length);
            return (
              <span key={debtor.id} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${entry ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                {entry && <CheckCircleIcon className="h-3.5 w-3.5" />}
                Debtor {debtorNumber(debtor)}: {entry ? 'created' : 'not created'}
              </span>
            );
          })}
        </div>
      )}

      {selectedDebtor && currentEntry && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-green-200 bg-white p-3">
          <span className="mr-1 inline-flex items-center gap-1 text-sm font-semibold text-green-700">
            <CheckCircleIcon className="h-4 w-4" />
            Form created{currentEntry.packet.status === 'sent_to_client' ? ' and emailed' : ''}
          </span>
          {currentEntry.packet.generated_pdf && (
            <button type="button" onClick={() => openGeneratedDocument(currentEntry.packet, 'pdf')} className="inline-flex items-center gap-1.5 rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50">
              <ArrowDownTrayIcon className="h-4 w-4" /> Open PDF
            </button>
          )}
          {currentEntry.packet.generated_docx && (
            <button type="button" onClick={() => openGeneratedDocument(currentEntry.packet, 'docx')} className="inline-flex items-center gap-1.5 rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50">
              <ArrowDownTrayIcon className="h-4 w-4" /> Open DOCX
            </button>
          )}
          <button type="button" onClick={() => openEmailComposer(currentEntry, selectedDebtor)} disabled={loadingEmailContext} className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
            {loadingEmailContext
              ? <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Preparing…</>
              : <><EnvelopeIcon className="h-4 w-4" /> Compose email</>}
          </button>
        </div>
      )}

      {selectedDebtor && !currentEntry && (
        <form onSubmit={createForm} className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h5 className="font-semibold text-slate-900">
                Create Chapter {chapter} Debtor {debtorNumber(selectedDebtor)} DSO Form
              </h5>
              <p className="mt-1 text-xs text-slate-500">{fullName(selectedDebtor)}</p>
            </div>
            <DocumentPlusIcon className="h-5 w-5 text-indigo-600" />
          </div>

          {!template && !loading && (
            <p className="mt-3 text-sm text-amber-800">
              Add or activate the “DSO Form Ch {chapter} Debtor {debtorNumber(selectedDebtor)}” template under Trustee Doc Templates.
            </p>
          )}

          {schema.length > 0 && (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {schema.map((field) => {
                const value = payload[field.key];
                const label = (
                  <>{field.label}{field.required && <span className="text-red-600"> *</span>}</>
                );
                if (field.type === 'textarea') {
                  return (
                    <label key={field.key} className="text-sm font-medium text-slate-700 sm:col-span-2">
                      {label}
                      <textarea className={inputClass} rows="3" value={value ?? ''} onChange={(event) => setPayload((current) => ({ ...current, [field.key]: event.target.value }))} />
                      {field.help && <span className="mt-1 block text-xs font-normal text-slate-500">{field.help}</span>}
                    </label>
                  );
                }
                if (field.type === 'checkbox') {
                  return (
                    <label key={field.key} className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <input type="checkbox" checked={Boolean(value)} onChange={(event) => setPayload((current) => ({ ...current, [field.key]: event.target.checked }))} />
                      {label}
                    </label>
                  );
                }
                return (
                  <label key={field.key} className="text-sm font-medium text-slate-700">
                    {label}
                    <input
                      className={inputClass}
                      type={field.type === 'number' || field.type === 'currency' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                      step={field.type === 'currency' ? '0.01' : undefined}
                      value={value ?? ''}
                      onChange={(event) => setPayload((current) => ({ ...current, [field.key]: event.target.value }))}
                    />
                    {field.help && <span className="mt-1 block text-xs font-normal text-slate-500">{field.help}</span>}
                  </label>
                );
              })}
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <button type="submit" disabled={creating || !template} className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
              {creating
                ? <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Creating…</>
                : <><DocumentPlusIcon className="h-4 w-4" /> Create DSO Form</>}
            </button>
          </div>
        </form>
      )}

      {emailComposer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" role="dialog" aria-modal="true" aria-labelledby="dso-email-title">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h2 id="dso-email-title" className="text-lg font-semibold text-slate-900">Email DSO Form</h2>
                <p className="mt-1 text-sm text-slate-500">
                  From {emailComposer.assignedEmployee.first_name} at Cisowski Law to {emailComposer.debtorName}.
                </p>
              </div>
              <button type="button" disabled={sendingEmail} onClick={() => setEmailComposer(null)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100" aria-label="Close email composer">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={sendEmail} className="space-y-4 px-5 py-5">
              <label className="block text-sm font-medium text-slate-700">
                To
                <input className={inputClass} value={emailComposer.to} onChange={(event) => setEmailComposer((current) => ({ ...current, to: event.target.value }))} required autoFocus />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Attorney CC (automatically included)
                <input className={`${inputClass} bg-slate-100`} value={emailComposer.cc} readOnly />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Subject
                <input className={inputClass} maxLength="255" value={emailComposer.subject} onChange={(event) => setEmailComposer((current) => ({ ...current, subject: event.target.value }))} required />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Message
                <textarea className={inputClass} rows="9" maxLength="50000" value={emailComposer.body} onChange={(event) => setEmailComposer((current) => ({ ...current, body: event.target.value }))} required />
              </label>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                <strong>Attachment:</strong>{' '}
                {emailComposer.entry.packet.generated_pdf ? 'DSO Form PDF' : 'DSO Form DOCX'}
              </div>
              <div className="flex flex-col-reverse gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end">
                <button type="button" disabled={sendingEmail} onClick={() => setEmailComposer(null)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={sendingEmail} className="inline-flex items-center justify-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
                  {sendingEmail
                    ? <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Sending…</>
                    : <><PaperAirplaneIcon className="h-4 w-4" /> Send email</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
